import * as React from 'react';
import { ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatE164RuToPretty } from '../../utils';
import type { ProfileForm } from './types';
import type { PresentatorItem } from './types';
import { Separator } from '@/components/ui/separator';
import { ProfileStructuredContent } from './ProfileStructuredContent';
import {
  profileContentLayoutForItem,
  sortProfileItems,
  tourTargetForItemName,
  type ProfileContentLayout,
} from './profileText';

type RegisterTourTarget = (id: string, el: HTMLElement | null) => void;

function ProfileField({
  label,
  value,
  layout = 'default',
  registerTourTarget,
}: {
  label: string;
  value: string;
  layout?: ProfileContentLayout;
  registerTourTarget?: RegisterTourTarget;
}) {
  const tourTarget = tourTargetForItemName(label);
  const titleRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (tourTarget) registerTourTarget?.(tourTarget, el);
    },
    [tourTarget, registerTourTarget],
  );

  if (!value.trim()) return null;

  return (
    <div>
      <div
        ref={tourTarget ? titleRef : undefined}
        data-tour-target={tourTarget ?? undefined}
        className="border-b border-neutral-border-300 pb-1.5 mb-2 text-h3 font-medium text-neutral-text-900"
      >
        {label}
      </div>
      <ProfileStructuredContent
        value={value}
        layout={layout}
        registerTourTarget={registerTourTarget}
      />
    </div>
  );
}

export interface ProfileFormProps {
  profileForm: ProfileForm;
  username: string;
  profileId: string;
  onUpdateFormItem?: (args: {
    profileId: string;
    username: string;
    telephoneE164: string;
    key?: string | null;
    item: PresentatorItem;
  }) => Promise<void>;
  registerTourTarget?: RegisterTourTarget;
}

export function ProfileForm({
  profileForm,
  username,
  profileId,
  onUpdateFormItem,
  registerTourTarget,
}: ProfileFormProps) {

  const commentData = profileForm.comment?.item.data || '';
  const [isEditingComment, setIsEditingComment] = React.useState(false);
  const [draftComment, setDraftComment] = React.useState(commentData);
  const [isSavingComment, setIsSavingComment] = React.useState(false);
  const [prevCommentData, setPrevCommentData] = React.useState(commentData);

  if (prevCommentData !== commentData) {
    setPrevCommentData(commentData);
    setDraftComment(commentData);
  }

  const handleSaveComment = async () => {
    if (!profileForm.comment?.key || !onUpdateFormItem) return;
    setIsSavingComment(true);
    try {
      await onUpdateFormItem({
        profileId,
        username,
        telephoneE164: profileForm.telephone || '',
        key: profileForm.comment.key,
        item: { ...profileForm.comment.item, data: draftComment },
      });
      setIsEditingComment(false);
    } catch {
      // Global mutation toast; keep editor open so the user can retry.
    } finally {
      setIsSavingComment(false);
    }
  };

  const telephone = profileForm.telephone ? formatE164RuToPretty(profileForm.telephone) : '';

  const [isExpanded, setIsExpanded] = React.useState(false);

  const expandRef = React.useCallback(
    (el: HTMLButtonElement | null) => {
      registerTourTarget?.('expand-profile', el);
    },
    [registerTourTarget],
  );

  const sortedItems = sortProfileItems(profileForm.items);
  const standardItems = sortedItems.filter((entry) => entry.item.ui_type !== 'toggle');
  const collapsibleItems = sortedItems.filter((entry) => entry.item.ui_type === 'toggle');

  return (
    <div className="space-y-4">
      <div className="rounded-l border p-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <div className="text-h3 font-medium">{profileForm.fio || 'Неизвестный'}</div>
            </div>
            <div>
              <div className="text-body1 text-neutral-text-500">Телефон</div>
              <div className="mt-1 text-body1">{telephone || '+7 (___) ___-__-__'}</div>
            </div>
          </div>
        </div>

        <div className={cn('', collapsibleItems.length > 0 && isExpanded ? 'pb-4 mt-4' : 'mt-4')}>
          <div className="flex items-center gap-2 text-body1 text-neutral-text-500">
            <span>Комментарий</span>
            {!isEditingComment && profileForm.comment?.key && onUpdateFormItem && (
              <IconButton
                variant="ghost"
                size="xs"
                onClick={() => setIsEditingComment(true)}
                className="h-auto p-0"
                aria-label="Редактировать комментарий"
              >
                <Pencil strokeWidth={2.5} className="size-3.5" />
              </IconButton>
            )}
            {isEditingComment && (
              <div className="ml-1 flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-neutral-text-500 hover:text-neutral-text-800"
                  onClick={handleSaveComment}
                  loading={isSavingComment}
                >
                  {!isSavingComment ? <Check size={14} /> : null}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-neutral-text-500 hover:text-neutral-text-800"
                  onClick={() => {
                    setDraftComment(profileForm.comment?.item.data || '');
                    setIsEditingComment(false);
                  }}
                  disabled={isSavingComment}
                >
                  <X size={14} />
                </Button>
              </div>
            )}
          </div>
          {isEditingComment ? (
            <Textarea
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
              disabled={isSavingComment}
              className="mt-2 text-body1 min-h-25 bg-neutral-background-100! w-full resize-y focus-visible:ring-1!"
              autoFocus
            />
          ) : (
            <div className="mt-1 text-body1 whitespace-pre-wrap">{profileForm.comment?.item.data || 'Нет комментария'}</div>
          )}

          {standardItems.length > 0 ? (
            <div className="mt-3 space-y-3">
              {standardItems.map((entry, idx) => (
                <ProfileField
                  key={`${entry.item.name}-standard-${idx}`}
                  label={entry.item.name as string}
                  value={entry.item.data as string}
                  layout={profileContentLayoutForItem(entry.item.name as string)}
                  registerTourTarget={registerTourTarget}
                />
              ))}
            </div>
          ) : null}
        </div>

        {isExpanded && collapsibleItems.length > 0 ? (<>
          <Separator className="mb-3" />
          <div className="mt-1 space-y-3">
            {collapsibleItems.map((entry, idx) => (
              <ProfileField
                key={`${entry.item.name}-collapsible-${idx}`}
                label={entry.item.name as string}
                value={entry.item.data as string}
                layout={profileContentLayoutForItem(entry.item.name as string)}
                registerTourTarget={registerTourTarget}
              />
            ))}
          </div>
        </>
        ) : null}

        {collapsibleItems.length > 0 ? (
          <Button
            ref={expandRef}
            type="button"
            variant="ghost"
            data-tour-target="expand-profile"
            className="mt-4 inline-flex h-auto items-center p-0 text-neutral-text-500 text-body1 font-medium hover:bg-transparent"
            onClick={() => setIsExpanded((v) => !v)}
          >
            <span>{isExpanded ? 'Свернуть' : 'Развернуть'}</span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-neutral-text-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-neutral-text-500" />
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
